import React, { useState, useEffect } from 'react';
import CustomCheckbox from '../CustomCheckBox';
import { NotificationSettingsData, NotificationSettingsGroup } from 'src/shared/types/src';
import settingsUIData from './notificationSettingUI';
import { NOTIFICATION_SETTINGS_KEYS } from 'src/shared/config/src';
import CustomButton from '../CustomButton';

type NotificationSettingsUIProps = {
    notificationSettingsData: NotificationSettingsData,
    isEmailEnabled: boolean
    submitSettings: (payload: NotificationSettingsData,
        successCallBack: () => void,
        callBack: () => void
    ) => void
}

const useBeforeUnload = ({ when, message }: any) => {
    useEffect(() => {
        const handleBeforeUnload = (event: any) => {
            event.preventDefault()
            event.returnValue = message
            return message
        }

        if (when) {
            window.addEventListener('beforeunload', handleBeforeUnload)
        }

        return () => window.removeEventListener('beforeunload', handleBeforeUnload)
    }, [when, message])
}

const NotificationSettingsUI: React.FC<NotificationSettingsUIProps> = ({ notificationSettingsData, isEmailEnabled,
    submitSettings
}) => {

    const [savingLoader, setSavingLoader] = useState<boolean>(false);
    const [isEmailNotificationEnabled, setIsEmailNotificationEnabled] = useState<boolean>(isEmailEnabled);
    const [isEmailNotificationPartiallySelected, setIsEmailNotificationPartiallySelected] = useState<boolean>(false);
    const [haveUnsavedChanges, setHaveUnsavedChanges] = useState(false);
    const [notificationSettingsMap, setNotificationSettingsMap] = useState<Map<string, boolean>>(new Map([
        [NOTIFICATION_SETTINGS_KEYS.FINANCIALS_INVOICES_PAYMENTS, false],
        [NOTIFICATION_SETTINGS_KEYS.FINANCIALS_OVERDUE_BALANCE_STATEMENTS, false],
        [NOTIFICATION_SETTINGS_KEYS.UPDATES_COMMUNICATION_MESSAGES, false],
        [NOTIFICATION_SETTINGS_KEYS.UPDATES_COMMUNICATION_OBSERVATIONS, false],
        [NOTIFICATION_SETTINGS_KEYS.UPDATES_COMMUNICATION_DAILY_DIARIES, false],
        [NOTIFICATION_SETTINGS_KEYS.UPDATES_COMMUNICATION_NEWSFEED, false],
        [NOTIFICATION_SETTINGS_KEYS.UPDATES_COMMUNICATION_SESSIONS, false],
        [NOTIFICATION_SETTINGS_KEYS.WELLBEING_INCIDENT_ACCIDENT_SHEETS, false],
        [NOTIFICATION_SETTINGS_KEYS.WELLBEING_MEDICINE_FORMS, false],
        [NOTIFICATION_SETTINGS_KEYS.REPORTS_RESOURCES_REPORTS, false],
        [NOTIFICATION_SETTINGS_KEYS.REPORTS_RESOURCES_PLANNING_SHEETS, false],
        [NOTIFICATION_SETTINGS_KEYS.REPORTS_RESOURCES_RESOURCES_DOCUMENTS, false],
    ]));

    useEffect(() => {
        populateDataInUIModels(notificationSettingsData);
        setIsEmailNotificationEnabled(isEmailEnabled);
    }, [notificationSettingsData])

    useEffect(() => {
        let anySettingIsEnabled = false;

        for (const [, value] of notificationSettingsMap) {
            if (value === true) {
                anySettingIsEnabled = true;
                break;
            }
        }

        let anySettingIsDisabled = false;
        for (const [, value] of notificationSettingsMap) {
            if (value === false) {
                anySettingIsDisabled = true;
                break;
            }
        }

        setIsEmailNotificationEnabled(anySettingIsEnabled);
        setIsEmailNotificationPartiallySelected(anySettingIsDisabled);
    }, [notificationSettingsMap]);

    useBeforeUnload({
        when: haveUnsavedChanges,
        message: 'Are you sure you want to leave?',
    })

    const generateNotificationSettingsPayload = () => {
        const requestPayload: NotificationSettingsData = {
            emailNotfications: {
                types: {
                    FINANCIALS: {
                        INVOICES_PAYMENTS: notificationSettingsMap.get(NOTIFICATION_SETTINGS_KEYS.FINANCIALS_INVOICES_PAYMENTS) || false,
                        OVERDUE_BALANCE_STATEMENTS: notificationSettingsMap.get(NOTIFICATION_SETTINGS_KEYS.FINANCIALS_OVERDUE_BALANCE_STATEMENTS) || false,
                    },
                    UPDATES_COMMUNICATION: {
                        MESSAGES: notificationSettingsMap.get(NOTIFICATION_SETTINGS_KEYS.UPDATES_COMMUNICATION_MESSAGES) || false,
                        OBSERVATIONS: notificationSettingsMap.get(NOTIFICATION_SETTINGS_KEYS.UPDATES_COMMUNICATION_OBSERVATIONS) || false,
                        DAILY_DIARIES: notificationSettingsMap.get(NOTIFICATION_SETTINGS_KEYS.UPDATES_COMMUNICATION_DAILY_DIARIES) || false,
                        NEWSFEED: notificationSettingsMap.get(NOTIFICATION_SETTINGS_KEYS.UPDATES_COMMUNICATION_NEWSFEED) || false,
                        SESSIONS: notificationSettingsMap.get(NOTIFICATION_SETTINGS_KEYS.UPDATES_COMMUNICATION_SESSIONS) || false,
                    },
                    WELLBEING: {
                        INCIDENT_ACCIDENT_SHEETS: notificationSettingsMap.get(NOTIFICATION_SETTINGS_KEYS.WELLBEING_INCIDENT_ACCIDENT_SHEETS) || false,
                        MEDICINE_FORMS: notificationSettingsMap.get(NOTIFICATION_SETTINGS_KEYS.WELLBEING_MEDICINE_FORMS) || false,
                    },
                    REPORTS_RESOURCES: {
                        REPORTS: notificationSettingsMap.get(NOTIFICATION_SETTINGS_KEYS.REPORTS_RESOURCES_REPORTS) || false,
                        PLANNING_SHEETS: notificationSettingsMap.get(NOTIFICATION_SETTINGS_KEYS.REPORTS_RESOURCES_PLANNING_SHEETS) || false,
                        RESOURCES_DOCUMENTS: notificationSettingsMap.get(NOTIFICATION_SETTINGS_KEYS.REPORTS_RESOURCES_RESOURCES_DOCUMENTS) || false,
                    }
                }
            },
            pushNotifications: true
        }
        return requestPayload;
    }

    const populateDataInUIModels = (data: NotificationSettingsData) => {
        setNotificationSettingsMap((prevState: any) => (new Map([
            ...prevState.entries(),
            [NOTIFICATION_SETTINGS_KEYS.FINANCIALS_INVOICES_PAYMENTS, data?.emailNotfications?.types?.FINANCIALS?.INVOICES_PAYMENTS || false],
            [NOTIFICATION_SETTINGS_KEYS.FINANCIALS_OVERDUE_BALANCE_STATEMENTS, data?.emailNotfications?.types?.FINANCIALS?.OVERDUE_BALANCE_STATEMENTS || false],
            [NOTIFICATION_SETTINGS_KEYS.UPDATES_COMMUNICATION_MESSAGES, data?.emailNotfications?.types?.UPDATES_COMMUNICATION?.MESSAGES || false],
            [NOTIFICATION_SETTINGS_KEYS.UPDATES_COMMUNICATION_OBSERVATIONS, data?.emailNotfications?.types?.UPDATES_COMMUNICATION?.OBSERVATIONS || false],
            [NOTIFICATION_SETTINGS_KEYS.UPDATES_COMMUNICATION_DAILY_DIARIES, data?.emailNotfications?.types?.UPDATES_COMMUNICATION?.DAILY_DIARIES || false],
            [NOTIFICATION_SETTINGS_KEYS.UPDATES_COMMUNICATION_NEWSFEED, data?.emailNotfications?.types?.UPDATES_COMMUNICATION?.NEWSFEED || false],
            [NOTIFICATION_SETTINGS_KEYS.UPDATES_COMMUNICATION_SESSIONS, data?.emailNotfications?.types?.UPDATES_COMMUNICATION?.SESSIONS || false],
            [NOTIFICATION_SETTINGS_KEYS.WELLBEING_INCIDENT_ACCIDENT_SHEETS, data?.emailNotfications?.types?.WELLBEING?.INCIDENT_ACCIDENT_SHEETS || false],
            [NOTIFICATION_SETTINGS_KEYS.WELLBEING_MEDICINE_FORMS, data?.emailNotfications?.types?.WELLBEING?.MEDICINE_FORMS || false],
            [NOTIFICATION_SETTINGS_KEYS.REPORTS_RESOURCES_REPORTS, data?.emailNotfications?.types?.REPORTS_RESOURCES?.REPORTS || false],
            [NOTIFICATION_SETTINGS_KEYS.REPORTS_RESOURCES_PLANNING_SHEETS, data?.emailNotfications?.types?.REPORTS_RESOURCES?.PLANNING_SHEETS || false],
            [NOTIFICATION_SETTINGS_KEYS.REPORTS_RESOURCES_RESOURCES_DOCUMENTS, data?.emailNotfications?.types?.REPORTS_RESOURCES?.RESOURCES_DOCUMENTS || false],
        ])));
    }

    const handleSubmit = () => {
        if (!haveUnsavedChanges) return;
        setSavingLoader(true);
        const payload = generateNotificationSettingsPayload();
        submitSettings(payload, () => setHaveUnsavedChanges(false), () => setSavingLoader(false));
    }

    const onEmailNotificationSwitchValueChange = (e: any) => {
        let selection = e?.target?.checked;

        if(isEmailNotificationPartiallySelected){
            selection = true
        }
        setNotificationSettingsMap((prevState) => {
            const updatedMap = new Map(prevState);
            updatedMap.forEach((value, key) => {
                updatedMap.set(key, selection);
            });
            return updatedMap;
        });
        setIsEmailNotificationEnabled(selection);
        setHaveUnsavedChanges(true);
    };

    const onNotificationSettingsSwitchValueChange = (value: boolean, settingsKey: string) => {
        setNotificationSettingsMap((prevState: any) => (new Map([
            ...prevState.entries(),
            [settingsKey, value]
        ])));

        setHaveUnsavedChanges(true);
    };

    const renderEmailNotification = () => {
        return (
            <div className='d-flex align-items-center notification-ui'>
                <div className='notification-text fw-bold'>Email Notifications</div>
                <CustomCheckbox isChecked={isEmailNotificationEnabled} intermediate={isEmailNotificationPartiallySelected} onChange={onEmailNotificationSwitchValueChange} />
            </div>
        );
    };

    const renderSettingsSection = (sectionName: string) => {
        return (
            <div className='notification-ui-group'>{sectionName}</div>
        )
    }

    const renderSettingsItem = (title: string, value: boolean, settingsKey: string, onValueChange: (value: boolean, settingsKey: string) => void) => {
        return (
            <div className='d-flex align-items-center notification-ui'>
                <div className='notification-text'>{title}</div>
                <CustomCheckbox isChecked={value} onChange={(e) => onValueChange(e?.target?.checked, settingsKey)} />
            </div>
        );
    };

    const renderSettingGroup = (group: NotificationSettingsGroup) => {
        return (
            <>
                {renderSettingsSection(group.groupName)}
                {group.notificationSettings.map((setting) => renderSettingsItem(setting.title, notificationSettingsMap.get(`${group.groupKey}.${setting.settingsKey}`) || false, `${group.groupKey}.${setting.settingsKey}`, onNotificationSettingsSwitchValueChange))}
            </>
        )
    }

    return (
        <>
            <div>
                {renderEmailNotification()}
                {settingsUIData.map((item) => renderSettingGroup(item))}
            </div>
            <CustomButton text={'Save'} onClick={handleSubmit} isLoading={savingLoader} color={'primary'} disabled={!haveUnsavedChanges}/>
        </>
    )
}

export default NotificationSettingsUI